<script setup>
import { ref, onMounted, reactive, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import { verifyMd5 } from '@/helpers/string';

const route = useRoute();
const { proxy } = getCurrentInstance()

const transactionCode = route.params.transaction_code;
const hash = route.params.hash;

const info = reactive({})
const date = reactive({
  create: '',
  play: '',
  done: '',
})

const state = ref('')
const list = ref([])
const details = ref([])
const allBet = ref(0);
const allWin = ref(0);
const allTie = ref(0);
const allCancel = ref(0);
const beforeAmount = ref(0);
const afterAmount = ref(0);

const fetch = async () => {
  const res = await proxy.$API.transaction.getResult({
    transaction_code : transactionCode
  })

  if (res.code !== 0) {
    alert(res.msg)
    window.close()
  }

  if (!res.result.list || !res.result.list.length === 0 || !res.result.details || res.result.details.length === 0) {
    alert('데이터가 존재 하지 않습니다.')
    window.close()
  }

  const verifyKey = transactionCode + '878184ed37ef29322b414dcf7cb7e847';
  if (!verifyMd5(verifyKey, hash)) {
    alert('잘못된 접근 입니다.')
    window.close()
  }

  Object.assign(info, res.result.list[0]);
  list.value = res.result.list;
  details.value = res.result.details[0].detail;

  const betList = list.value.filter(x => x.type === 'bet');
  const winOrLossList = list.value.filter(x => x.type !== 'bet');
  allBet.value = betList.reduce((sum, bet) => sum + Number(bet.amount), 0);
  allWin.value = winOrLossList.reduce((sum, bet) => sum + Number(bet.amount), 0);
  allTie.value = allBet.value === allWin.value ? allWin.value : 0;

  beforeAmount.value = betList[0].beforeAmount;
  afterAmount.value = betList[betList.length - 1].afterAmount;
  state.value = winOrLossList ? '완료' : '배팅중';

  date.create = winOrLossList ? new Date(winOrLossList[0].data.created_at).toLocaleString('ko-KR') : '';
  date.play = winOrLossList ? new Date(winOrLossList[0].data.played_at).toLocaleString('ko-KR') : '';
  date.done = winOrLossList ? new Date(winOrLossList[0].data.settled_at).toLocaleString('ko-KR') : '';

}

onMounted( async () => {
  await fetch();
})

</script>

<template>
  <div class="baccarat-container">
    <div class="wrapper">
      <!-- 헤더 섹션 -->
      <div class="header-section">
        <div class="game-logo">
          <img src="https://static.miraclegame.app/production/game/1734941693_67691bfd24d7d.png" alt="게임 로고" class="logo-img" />
          <div class="game-title">
            <div class="game-provider">드림게이밍 카지노(DreamGaming Casino)</div>
            <h1 class="game-name">{{ info.gameName }}</h1>
          </div>
        </div>
      </div>

      <!-- 메인 콘텐츠 -->
      <div class="wrapper-content">
        <!-- 왼쪽 패널: 라운드 정보 -->
        <div class="left-panel">
          <h2 class="panel-title">라운드 정보</h2>
          <div class="info-table">
            <div class="info-row">
              <div class="info-label">통합코드</div>
              <div class="info-value">{{ transactionCode }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">게임코드</div>
              <div class="info-value">{{ info.game }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">라운드키</div>
              <div class="info-value">{{ info.siteRoundId }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">베팅</div>
              <div class="info-value">{{ allBet.toLocaleString() }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">승</div>
              <div class="info-value">{{ allWin.toLocaleString() }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">무</div>
              <div class="info-value">{{ allTie.toLocaleString() }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">취소</div>
              <div class="info-value">{{ allCancel.toLocaleString() }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">프로모션</div>
              <div class="info-value">0</div>
            </div>
            <div class="info-row">
              <div class="info-label">상태</div>
              <div class="info-value"><span class="status-tag success"> {{ state }}</span></div>
            </div>
            <div class="info-row">
              <div class="info-label">생성일</div>
              <div class="info-value">{{ date.create }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">베팅일시</div>
              <div class="info-value">{{ date.play }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">완료일시</div>
              <div class="info-value">{{ date.done }}</div>
            </div>
          </div>

          <h2 class="panel-title mt-6">회원 정보</h2>
          <div class="info-table">
            <div class="info-row">
              <div class="info-label">아이디</div>
              <div class="info-value">{{ info.username }} </div>
            </div>
            <div class="info-row">
              <div class="info-label">사이트 아이디</div>
              <div class="info-value">{{ info.siteUsername }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">베팅전 잔액</div>
              <div class="info-value">{{ beforeAmount.toLocaleString() }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">베팅후 잔액</div>
              <div class="info-value">{{ afterAmount.toLocaleString() }}</div>
            </div>
          </div>
        </div>

        <!-- 오른쪽 패널: 게임 결과 -->
        <div class="right-panel">
          <h2 class="panel-title">게임 결과</h2>
          <div class="result-tabs">
            <div class="tab">베팅정보</div>
            <div class="tab">결과정보</div>
          </div>

          <!-- 베팅 정보 테이블 -->
          <div class="betting-info">
            <table class="betting-table">
              <thead>
              <tr>
                <th class="hth">베팅위치</th>
                <th class="hth">베팅금액</th>
                <th class="hth">당첨금</th>
                <th class="hth">결과</th>
              </tr>
              </thead>
              <tbody>
              <template v-for="(data, index) in details.bets" :key="index">
                <tr class="highlight-row">
                  <td>{{ data.target }}</td>
                  <td>{{ data.betAmount.toLocaleString() }}</td>
                  <td>{{ data.winAmount.toLocaleString() }}</td>

                  <!-- 카드 영역은 첫 번째 루프에서만 보여줌 -->
                  <td
                    v-if="index === 0"
                    class="cards-cell"
                    :rowspan="details.bets.length"
                  >
                    <div class="cards-wrapper">
                      <div class="cards-p" :style="{ backgroundColor: details.result.playerScore > details.result.bankerScore ? '#e4ecff' : 'transparent' }" >
                        <div class="player-label"><strong>Player</strong></div>
                        <div class="cards">
                          <div class="card" v-for="(c, i) in details.result.playerCards" :key="i">
                            <img :src="require(`@/assets/images/cards/${c}.png`)" class="card-image"/>
                          </div>
                          <div class="card-value"><strong>{{details.result.playerScore}}</strong></div>
                        </div>
                      </div>
                      <div class="cards-b" :style="{ backgroundColor: details.result.bankerScore > details.result.playerScore ? '#ffeaea' : 'transparent' }">
                        <div class="banker-label"><strong>Banker</strong></div>
                        <div class="cards">
                          <div class="card" v-for="(c, i) in details.result.bankerCards" :key="i">
                            <img :src="require(`@/assets/images/cards/${c}.png`)" class="card-image"/>
                          </div>
                          <div class="card-value"><strong>{{details.result.bankerScore}}</strong></div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              </tbody>
            </table>
          </div>

          <h2 class="panel-title mt-6">게임 내역</h2>
          <div class="game-history">
            <table class="history-table">
              <thead>
              <tr>
                <th>코드</th>
                <th>형식</th>
                <th>이전 보유금</th>
                <th>금액</th>
                <th>이후 보유금</th>
                <th>생성일</th>
                <th>완료일</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(data, index) in list" :key="index">
                <td>
                  <div class="code-cell">
                    <span class="code-label">개별코드</span>
                    <span>{{data.data.code}}</span>
                  </div>
                  <div class="code-cell">
                    <span class="code-label">통합코드</span>
                    <span>{{data.data.transaction_code}}</span>
                  </div>
                </td>
                <td>
                  <div class="action-tag bet">{{ data.type }}</div>
                </td>
                <td>{{ data.beforeAmount.toLocaleString()}}</td>
                <td>{{ data.amount.toLocaleString()}}</td>
                <td>{{ data.afterAmount.toLocaleString()}}</td>
                <td>{{ new Date(data.createdAt).toLocaleString('ko-KR')}}</td>
                <td>{{ data.data.settled_at ? new Date(data.data.settled_at).toLocaleString('ko-KR') : new Date(data.createdAt).toLocaleString('ko-KR')}}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<style scoped>
.tr {

}
.baccarat-container {
  font-family: 'Noto Sans KR', sans-serif;
  background-color: #f5f5f7;
  padding: 20px;
  color: #333;
}

.header-section {
  background-color: #f0f0f2;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.game-logo {
  display: flex;
  align-items: center;
}

.logo-img {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  margin-right: 16px;
}

.game-provider {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.game-name {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}


.wrapper-content {
  display: flex;
  gap: 20px;
}

.left-panel, .right-panel {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.left-panel {
  flex: 1;
}

.right-panel {
  flex: 3;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.card-image{
  width: 23px;
  height: auto;
}
.mt-6 {
  margin-top: 24px;
}

.info-table {
  width: 100%;
}

.info-row {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  padding: 8px 0;
}

.info-label {
  flex: 1;
  font-weight: 500;
  color: #555;
}

.info-value {
  flex: 2;
  text-align: right;
}

.status-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.success {
  background-color: #e6f7e6;
  color: #2e7d32;
}

.result-tabs {
  width: 100%;
  display: flex;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.tab {
  width: 50%;
  text-align: center;
  padding: 8px 16px;
  cursor: pointer;
}

.tab.active {
  font-weight: 600;
  border-bottom: 2px solid #4a6cf7;
  color: #4a6cf7;
}

.betting-table, .history-table {
  width: 100%;
  border-collapse: collapse;
}

.betting-table th, .history-table th {
  background-color: #f9f9f9;
  padding: 10px;
  text-align: center;
  font-weight: 500;
  color: #555;
}

.betting-table td, .history-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
}

.highlight-row {
  background-color: #ffffff;
}

.cards-cell {
  padding: 16px;
  vertical-align: top;
}

.cards-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 16px;

}
.player-label, .banker-label {
  font-weight: 500;
  margin-bottom: 4px;
  margin-right: 5px;
  margin-left: 5px;
}

.cards-p{
  display: flex;
  flex-direction: column;
  align-items: flex-end;   /* 오른쪽 정렬 */
  justify-content: center;  /* 수직 중앙 */
  width: 48%;
  text-align: center;       /* 내부 텍스트 중앙 */
}
.cards-b {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* 왼쪽 정렬 */
  justify-content: center;  /* 수직 중앙 */
  width: 48%;
  text-align: center;       /* 내부 텍스트 중앙 */
}

.cards {
  display: flex;
  margin-bottom: 8px;
}

.card {
  display: inline-block;

  margin-left: 4px;
  padding: 6px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-value {
  font-size: 15px;
  height: 33%;
  margin-right: 8px;
  margin-left: 8px;
  font-weight: 800;
  width: 20px;
  background-color: white;
  border: 1px solid darkgray;
  border-radius: 6px;
}

.spade, .club {
  color: black;
}

.diamond, .heart {
  color: red;
}

.code-cell {
  font-size: 13px;
  margin-bottom: 4px;
  display: flex;
}

.code-label {
  display: inline-block;
  background-color: #e8f4fd;
  color: #0277bd;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 6px;
  font-size: 12px;
}

.action-tag {
  display: inline-block;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  text-align: center;
}

.bet {
  background-color: #5c6bc0;
}

.win {
  background-color: #5c6bc0;
}

.bonus-tag {
  color: #ff9800;
  font-size: 12px;
  margin-top: 4px;
}
</style>